import { AlertCircle, Phone } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import parsePhoneNumberFromString from 'libphonenumber-js';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button, Decorator, Input, Modal } from '@myhealth-raccoon/components';
import { DateInput, validation } from '@myhealth-raccoon/forms';

import { Hint } from '../Hint';
import { BackButton } from 'src/components/BackButton';
import { StickyBottomBar } from 'src/components/StickyBottomBar.tsx';
import { useClinicSelector } from 'src/hooks/useAsyncSelector';
import { useRememberFormValues } from 'src/hooks/useRememberFormValues';

type Props = {
    onSubmit: (values: PersonalDetailsFormValues) => void;
    onBack?(): void;
    className?: string;
    phoneNumber: string;
    forceBlank?: boolean;
};

export type PersonalDetailsFormValues = {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    mobileNumber: string;

    rememberMe: boolean;
    termsOfServiceConsent: boolean;
    cancellationPolicyConsent: boolean;
};

export const PersonalDetailsForm = React.forwardRef<HTMLFormElement, Props>(
    ({ onSubmit, onBack, className, phoneNumber, forceBlank }, ref) => {
        const [defaultValues, rememberDefaultValues] = useRememberFormValues<PersonalDetailsFormValues>(
            'personalDetailsForm',
            'local'
        );
        const [modalOpen, setModalOpen] = useState(false);

        const { data: clinic, ...clinicRequest } = useClinicSelector();

        const {
            handleSubmit: innerSubmit,
            register,
            control,
            formState: { isSubmitting, errors },
        } = useForm<PersonalDetailsFormValues>({ defaultValues: !forceBlank ? (defaultValues ?? {}) : undefined });

        register('dateOfBirth', {
            validate: validation.composeValidators(
                validation.required('Please enter a date of birth'),
                validation.dob('Please enter a valid date of birth')
            ),
        });

        return (
            <form
                className={classNames('flex flex-col', className)}
                ref={ref}
                onSubmit={innerSubmit(async (values) => {
                    rememberDefaultValues(values, 'session');
                    rememberDefaultValues(values.rememberMe ? values : undefined, 'local');
                    await onSubmit(values);
                })}
            >
                <div className="flex flex-1 flex-col gap-y-5 px-5 pt-5 lg:flex-none">
                    <Decorator
                        fieldset
                        label="Legal name"
                        hint="Enter name exactly as it is recorded with Medicare or an ID."
                        requiredIndicator
                    >
                        <Input
                            id="firstName"
                            {...register('firstName', {
                                validate: validation.composeValidators(
                                    validation.required('Please enter a first name'),
                                    validation.name()
                                ),
                            })}
                            fieldError={errors.firstName}
                            placeholder="First name"
                            className="mb-2.5"
                            type="text"
                            autoCorrect="off"
                            autoCapitalize="words"
                            spellCheck="false"
                        />
                        <Input
                            id="lastName"
                            {...register('lastName', {
                                validate: validation.composeValidators(
                                    validation.required('Please enter a last name'),
                                    validation.name()
                                ),
                            })}
                            fieldError={errors.lastName}
                            placeholder="Last name"
                            type="text"
                            autoCorrect="off"
                            autoCapitalize="words"
                            spellCheck="false"
                        />
                    </Decorator>

                    <DateInput
                        label="Date of birth"
                        control={control}
                        name="dateOfBirth"
                        id="dateOfBirth"
                        fieldError={errors.dateOfBirth}
                        requiredIndicator
                    />

                    <Input
                        id="mobileNumber"
                        {...register('mobileNumber', {
                            validate: validation.composeValidators(
                                validation.required('Please enter a mobile number'),
                                validation.phoneNumber('Please enter a valid mobile number')
                            ),
                        })}
                        fieldError={errors.mobileNumber}
                        type="tel"
                        placeholder="0412 345 678"
                        label="Mobile number"
                        requiredIndicator
                        hint={
                            <button
                                type="button"
                                onClick={() => setModalOpen(true)}
                                className="text-primary font-semibold hover:underline"
                            >
                                Don't have a mobile number?
                            </button>
                        }
                    />

                    <Decorator fieldError={errors.termsOfServiceConsent}>
                        <div className="flex flex-row items-start justify-start gap-2 text-sm font-semibold text-gray-700">
                            <input
                                className="checkbox mt-0.5"
                                id="termsOfServiceConsent"
                                {...register('termsOfServiceConsent', { validate: validation.required() })}
                                type="checkbox"
                            />
                            <label className="cursor-pointer text-base sm:text-sm" htmlFor="termsOfServiceConsent">
                                I consent to Myhealth's{' '}
                                <a
                                    target="_blank"
                                    href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}/privacy-policy`}
                                    className="text-primary underline"
                                >
                                    Privacy Policy
                                </a>{' '}
                                and our{' '}
                                <a
                                    target="_blank"
                                    href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}/information-collection-notice`}
                                    className="text-primary underline"
                                >
                                    Information Collection Policy
                                </a>
                                .{' '}
                                <span aria-label="Required" className="text-primary">
                                    *
                                </span>
                            </label>
                        </div>
                    </Decorator>

                    <Decorator fieldError={errors.cancellationPolicyConsent}>
                        <div className="flex flex-row items-start justify-start gap-2 text-sm font-semibold text-gray-700">
                            <input
                                className="checkbox mt-0.5"
                                id="cancellationPolicyConsent"
                                {...register('cancellationPolicyConsent', { validate: validation.required() })}
                                type="checkbox"
                            />
                            <label className="cursor-pointer text-base sm:text-sm" htmlFor="cancellationPolicyConsent">
                                I agree to the{' '}
                                <a
                                    target="_blank"
                                    href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}${clinic?.url}#pricing`}
                                    className="text-primary underline"
                                >
                                    Consultation Fees
                                </a>{' '}
                                &{' '}
                                <a
                                    target="_blank"
                                    href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}${clinic?.url}#cancellation-policy`}
                                    className="text-primary underline"
                                >
                                    Cancellation policy
                                </a>{' '}
                                of {clinic ? clinic?.name : clinicRequest.error ? clinicRequest.error.message : '...'}.{' '}
                                <span aria-label="Required" className="text-primary">
                                    *
                                </span>
                            </label>
                        </div>
                    </Decorator>
                </div>
                <StickyBottomBar
                    className={classNames(
                        onBack ? 'flex-row' : 'flex-col',
                        'justify-between gap-5 md:flex-row md:items-center'
                    )}
                >
                    <Decorator fieldError={errors.rememberMe}>
                        <div className="flex flex-row items-start justify-start gap-2 text-sm font-semibold text-gray-700">
                            <input
                                className="checkbox mt-0.5"
                                id="rememberMe"
                                {...register('rememberMe')}
                                type="checkbox"
                            />
                            <label className="cursor-pointer text-base sm:text-sm" htmlFor="rememberMe">
                                Remember details
                            </label>
                            <Hint
                                tooltipContent={
                                    <div className="text-xs font-medium">
                                        <p className="mb-1">
                                            <strong className="font-semibold">What is 'Remember details'?</strong>
                                        </p>
                                        <p>
                                            We’ll remember your details on this device. You’ll still need to verify with
                                            a one-time code each time you book.
                                        </p>
                                    </div>
                                }
                            />
                        </div>
                    </Decorator>
                    {onBack && <BackButton onClick={onBack} label="Back" />}
                    <Button className="w-full text-center md:w-fit" disabled={isSubmitting} type="submit">
                        Verify
                    </Button>
                </StickyBottomBar>
                <Modal
                    open={modalOpen}
                    backdropClassname="backdrop-blur-sm"
                    onClose={() => setModalOpen(false)}
                    icon={{ icon: AlertCircle, size: 'md' }}
                    size="md"
                    className="z-50"
                >
                    <p className="text-base">
                        <strong className="font-semibold text-gray-900">Please call the clinic</strong>
                    </p>
                    <p>To make a booking, please contact the clinic directly on {phoneNumber}.</p>
                    <div className="flex flex-col-reverse gap-3 pt-8 md:flex-row">
                        <Button onClick={() => setModalOpen(false)} color="gray" className="flex-1">
                            Close
                        </Button>
                        <Button
                            href={parsePhoneNumberFromString(phoneNumber, 'AU')?.getURI()}
                            color="primary"
                            className="flex-1 text-center"
                        >
                            <Phone className="inline-block size-5" /> {phoneNumber}
                        </Button>
                    </div>
                </Modal>
            </form>
        );
    }
);
